import React, { useState } from "react";
import {
  Box,
  Dialog,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  Close as CloseIcon,
  CallSplit as BranchIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";

import { MuiTelInput, MuiTelInputInfo } from "mui-tel-input";

import { toast } from "react-toastify";
import { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import {
  FieldLabel,
  StyledSelect,
  StyledTextField,
} from "../types/company-form";

interface AddBranchDrawerProps {
  open: boolean;
  onClose: () => void;
  companyId: number;
  onSuccess: () => void; // refetch branches after adding
}

interface BranchFormData {
  name: string;

  phone: string;
  email: string;
  city: string;
  address: string;
}

const EMPTY_FORM: BranchFormData = {
  name: "",

  phone: "",
  email: "",
  city: "",
  address: "",
};

export default function AddBranchDrawer({
  open,
  onClose,
  companyId,
  onSuccess,
}: AddBranchDrawerProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const cities: string[] =
    useSelector((state: RootState) => state.theme.cities) ?? [];
  const color100 = reduxTheme.primary_colors?.[100];
  const color300 = reduxTheme.primary_colors?.[300];
  const color400 = reduxTheme.primary_colors?.[400] ?? "#8B4513";
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color900 = reduxTheme.primary_colors?.[900];

  const [form, setForm] = useState<BranchFormData>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const set =
    (field: keyof BranchFormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handlePhoneChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    setForm((prev) => ({ ...prev, phone: value }));
  };

  const handleClose = () => {
    setForm(EMPTY_FORM);
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Branch name is required.");
      return;
    }

    setSubmitting(true);
    try {
      await apiService.post("api/companies/company-branch/", {
        company_id: companyId,
        name: form.name,

        phone_number: form.phone,
        email: form.email,
        street_address: form.address,
        city: form.city,
        country: "ZW",
        time_zone: "Africa/Harare",
        is_active: true,
      });
      toast.success("Branch added successfully!");
      onSuccess();
      handleClose();
    } catch (err) {
      console.error("Failed to add branch:", err);
      toast.error("Failed to add branch. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: color900,
          borderRadius: "20px",
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: `linear-gradient(135deg, ${color400} 0%, #c9834a 100%)`,
          borderRadius: "20px 20px 0 0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <BranchIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ color: "#fff" }}
          >
            Add Branch
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: "#fff" }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Form */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 3,
          py: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box>
            <FieldLabel textcolor={color100}>
              Branch Name <span className="required">*</span>
            </FieldLabel>
            <StyledTextField
              placeholder="e.g. Harare Branch"
              value={form.name}
              onChange={set("name")}
              inputbg={color800}
              textcolor={color700}
              placeholdercolor={color300}
            />
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Box>
            <FieldLabel textcolor={color100}>Phone</FieldLabel>
            <MuiTelInput
              value={form.phone}
              onChange={handlePhoneChange}
              defaultCountry="ZW"
              variant="outlined"
              MenuProps={{
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                disablePortal: true,
                PaperProps: { sx: { maxHeight: 250, mt: 1 } },
              }}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: color800,
                  borderRadius: "30px",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  fontSize: "13px",
                  color: color700,
                },
                "& .MuiInputBase-input": {
                  padding: "10px 14px",
                  fontSize: "13px",
                  color: color700,
                },
              }}
            />
          </Box>
          <Box>
            <FieldLabel textcolor={color100}>Email</FieldLabel>
            <StyledTextField
              placeholder="branch@company.com"
              value={form.email}
              onChange={set("email")}
              inputbg={color800}
              textcolor={color700}
              placeholdercolor={color300}
            />
          </Box>
        </Box>

        <Box>
          <FieldLabel textcolor={color100}>Address</FieldLabel>
          <StyledTextField
            placeholder="Street address"
            value={form.address}
            onChange={set("address")}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>

        <Box>
          <FieldLabel textcolor={color100}>City</FieldLabel>
          <FormControl fullWidth sx={{ maxWidth: 320 }}>
            <StyledSelect
              value={form.city}
              displayEmpty
              selectbg={color800}
              textcolor={color700}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, city: String(e.target.value) }))
              }
              renderValue={(selected) =>
                selected ? (
                  <span style={{ color: color700 }}>{selected as string}</span>
                ) : (
                  <span style={{ color: color300 }}>Select city...</span>
                )
              }
              MenuProps={{
                PaperProps: { sx: { maxHeight: 280 } },
              }}
            >
              <MenuItem value="">
                <em>Select city</em>
              </MenuItem>
              {cities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: color900,
          borderRadius: "0 0 20px 20px",
        }}
      >
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={{
            textTransform: "none",
            color: "text.secondary",
            textDecoration: "underline",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 4,
            bgcolor: color400,
            "&:hover": { bgcolor: color400 },
          }}
        >
          {submitting ? "Saving..." : "Add Branch"}
        </Button>
      </Box>
    </Dialog>
  );
}
